import React, { type ReactNode } from 'react';
import { Box, IconButton, Stack, Tooltip, alpha, useTheme } from '@mui/material';
import { CaptionText, CompactPageTitle } from '../typography';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import { borderRadius, getControlBorder, getControlBorderColor, layoutGap } from '../../../theme/theme';
import type { HelpValue } from '../common/helpModel';
import HelpTooltipTrigger from '../common/HelpTooltipTrigger';
import ActiveDocumentName from '../feedback/ActiveDocumentName';
import RuntimeModeBadge from '../feedback/RuntimeModeBadge';
import { RuntimeVersionButton } from '../feedback/RuntimeVersionInfo';
import type { WorkflowChromePalette } from './WorkflowStepComponents';
import ThemeModeToggle from './ThemeModeToggle';

export interface WizardAppChromeBarUser {
  username?: string | null;
}

export interface WizardAppChromeBarProps {
  showTitleBlock: boolean;
  title: string;
  subtitle?: string;
  headerMeta?: ReactNode;
  compactTitle: boolean;
  compactChrome: boolean;
  /** When false, hide active Revit document chip (mapping workspace shell). */
  showActiveDocument: boolean;
  workflowStepper: ReactNode | null;
  utilityChrome?: ReactNode;
  rightToolsRef?: React.Ref<HTMLDivElement>;
  stepHelpTooltip?: HelpValue;
  showRuntimeBadgeInUtilityChrome: boolean;
  user: WizardAppChromeBarUser | null;
  isSigningOut: boolean;
  onSignOut: () => void;
  onClose: () => void;
  showWindowTools?: boolean;
  workflowPalette: WorkflowChromePalette;
}

/**
 * Wizard top bar: workflow dots (nav) flush left; page title directly to their right; spacer; then window tools.
 */
const WizardAppChromeBar: React.FC<WizardAppChromeBarProps> = ({
  showTitleBlock,
  title,
  subtitle,
  headerMeta,
  compactTitle,
  compactChrome,
  showActiveDocument,
  workflowStepper,
  utilityChrome,
  rightToolsRef,
  stepHelpTooltip,
  showRuntimeBadgeInUtilityChrome,
  user,
  isSigningOut,
  onSignOut,
  onClose,
  showWindowTools = true,
  workflowPalette,
}) => {
  const theme = useTheme();
  const chromeControlBorder = getControlBorder(theme);
  const chromeControlBorderColor = getControlBorderColor(theme);
  const moveDocumentToMetaRow = compactChrome && showActiveDocument;
  const activeDocument = showActiveDocument && !moveDocumentToMetaRow ? (
    <ActiveDocumentName
      compact={compactChrome}
    />
  ) : null;

  const rightTools = showWindowTools ? (
    <>
      {activeDocument}
      {utilityChrome}
      {stepHelpTooltip ? (
        <HelpTooltipTrigger
          label={title}
          help={stepHelpTooltip}
          placement="bottom-end"
          sx={{
            width: 24,
            height: 24,
            minWidth: 24,
            minHeight: 24,
            borderRadius: `${borderRadius.sm}px`,
            border: chromeControlBorder,
            backgroundColor: workflowPalette.subtleBackground,
            flexShrink: 0,
            '&:hover, &:focus-visible': {
              borderColor: chromeControlBorderColor,
            },
          }}
        />
      ) : null}
      {showRuntimeBadgeInUtilityChrome ? (
        <Box sx={{ flexShrink: 0 }}>
          <RuntimeModeBadge compact={compactChrome} showDocument={false} />
        </Box>
      ) : null}
      <RuntimeVersionButton />
      <ThemeModeToggle compact={compactChrome} />
      {user ? (
        <Tooltip title={`Sign out${user.username ? ` (${user.username})` : ''}`}>
          <span>
            <IconButton
              onClick={() => {
                void onSignOut();
              }}
              aria-label="Sign out"
              size="small"
              disabled={isSigningOut}
              sx={{
                flexShrink: 0,
                color: theme.palette.text.secondary,
                cursor: 'pointer',
                width: 22,
                height: 22,
                p: 0.25,
                borderRadius: `${borderRadius.sm}px`,
                border: chromeControlBorder,
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.warning.main, 0.14),
                  color: theme.palette.warning.dark,
                  borderColor: chromeControlBorderColor,
                },
                '&:focus-visible': {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: '2px',
                  borderColor: chromeControlBorderColor,
                },
                transition: 'all 0.2s ease',
              }}
            >
              <LogoutIcon sx={{ fontSize: 12.5 }} />
            </IconButton>
          </span>
        </Tooltip>
      ) : null}
      <IconButton
        onClick={onClose}
        aria-label="Close PipeEX"
        size="small"
        sx={{
          flexShrink: 0,
          color: theme.palette.text.secondary,
          cursor: 'pointer',
          width: 22,
          height: 22,
          p: 0.25,
          borderRadius: `${borderRadius.sm}px`,
          border: chromeControlBorder,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: alpha(theme.palette.error.main, 0.15),
            color: theme.palette.error.main,
            borderColor: chromeControlBorderColor,
          },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: '2px',
            borderColor: chromeControlBorderColor,
          },
          transition: 'all 0.2s ease',
        }}
      >
        <CloseIcon sx={{ fontSize: 13 }} />
      </IconButton>
    </>
  ) : null;

  return (
    <Box
      data-testid="main-layout-app-chrome"
      sx={{
        position: 'relative',
        zIndex: 10,
        mb: compactChrome ? 0.08 : 0.24,
        pb: compactChrome ? 0.06 : 0,
        borderBottom: `1px solid ${alpha(theme.palette.divider, compactChrome ? 0.06 : 0.08)}`,
      }}
    >
      <Stack
        spacing={showTitleBlock && (subtitle || headerMeta) && !compactChrome ? 0.3 : 0}
        sx={{ width: '100%', minWidth: 0 }}
      >
        <Stack
          direction="row"
          component="div"
          alignItems="center"
          data-testid="main-layout-top-chrome"
          spacing={compactChrome ? 0.45 : 0.85}
          useFlexGap
          sx={{
            width: '100%',
            minWidth: 0,
            flexWrap: compactChrome ? 'nowrap' : { xs: 'wrap', sm: 'nowrap' },
            minHeight: compactChrome ? 26 : 30,
            rowGap: compactChrome ? 0.12 : 0.32,
            py: compactChrome ? 0.04 : 0.2,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={compactChrome ? 0.45 : 0.85}
            sx={{
              flex: compactChrome ? '1 1 auto' : '0 1 auto',
              minWidth: 0,
              maxWidth: '100%',
              overflow: 'hidden',
              flexWrap: 'nowrap',
              pr: layoutGap.micro,
            }}
          >
            {workflowStepper ? (
              <Box
                sx={{
                  display: 'flex',
                  flex: { xs: '1 1 auto', sm: '0 1 auto' },
                  minWidth: compactChrome ? 56 : 0,
                  maxWidth: '100%',
                  overflowX: 'auto',
                  alignItems: 'center',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                }}
              >
                {workflowStepper}
              </Box>
            ) : null}
            {showTitleBlock ? (
              compactTitle ? (
                <CaptionText
                  data-testid="page-title"
                  sx={{
                    color: theme.palette.text.primary,
                    lineHeight: 1.1,
                    letterSpacing: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flex: '0 1 auto',
                    minWidth: 0,
                    maxWidth: compactChrome ? { xs: '34vw', sm: 'none' } : 'none',
                    textAlign: 'left',
                  }}
                >
                  {title}
                </CaptionText>
              ) : (
                <CompactPageTitle
                  data-testid="page-title"
                  sx={{
                    color: theme.palette.text.primary,
                    lineHeight: 1.1,
                    letterSpacing: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flex: '0 1 auto',
                    minWidth: 0,
                    maxWidth: compactChrome ? { xs: '34vw', sm: 'none' } : 'none',
                    textAlign: 'left',
                  }}
                >
                  {title}
                </CompactPageTitle>
              )
            ) : null}
          </Stack>
          <Box
            sx={{
              display: compactChrome ? 'none' : 'block',
              flex: '1 1 0%',
              minWidth: 0,
              alignSelf: 'stretch',
            }}
            aria-hidden
          />
          <Stack
            ref={rightToolsRef}
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            data-testid="main-layout-top-right-chrome"
            spacing={compactChrome ? 0.3 : 0.65}
            useFlexGap
            sx={{
              flex: compactChrome ? '0 0 auto' : { xs: '1 1 100%', sm: '0 1 auto' },
              minWidth: 0,
              maxWidth: '100%',
              flexWrap: compactChrome ? 'nowrap' : { xs: 'wrap', sm: 'nowrap' },
              rowGap: compactChrome ? 0.12 : 0.32,
            }}
          >
            {rightTools}
          </Stack>
        </Stack>
        {moveDocumentToMetaRow ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              minWidth: 0,
              maxWidth: '100%',
              pt: 0.04,
              overflow: 'hidden',
            }}
          >
            <ActiveDocumentName
              compact
              sx={{
                maxWidth: { xs: 'min(100%, 230px)', sm: 'min(46%, 280px)' },
              }}
            />
          </Box>
        ) : null}
        {showTitleBlock && subtitle ? (
          <CaptionText
            sx={{
              color: theme.palette.text.secondary,
              lineHeight: 1.25,
              maxWidth: { xs: '100%', md: 560 },
              display: compactChrome ? 'none' : compactTitle ? { xs: 'none', sm: 'block' } : 'block',
            }}
          >
            {subtitle}
          </CaptionText>
        ) : null}
        {showTitleBlock && headerMeta && !compactChrome ? (
          <Box
            sx={{
              mt: 0.15,
              width: '100%',
              maxWidth: 'min(100%, 720px)',
              display: 'flex',
              justifyContent: 'flex-start',
            }}
          >
            {headerMeta}
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
};

export default WizardAppChromeBar;
